import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { useIsOrgAdmin } from '../hooks/useIsOrgAdmin';
import NoAccess from '../components/NoAccess';
import { ALL_MODULE_KEYS, canAccessSection, type ModuleAccess, type ModuleKey } from '../utils/modules';

type ModuleAccessValue = ModuleAccess & { canAccess: (section: string) => boolean };

const OPEN: ModuleAccess = { loading: true, isAdmin: false, modules: null };

export const ModuleAccessContext = createContext<ModuleAccessValue>({
  ...OPEN,
  canAccess: (s) => canAccessSection(OPEN, s),
});

// Cargo do usuário logado: admin (regra do banco, via am_i_org_admin) e módulos.
export const ModuleAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  const { isAdmin, loading: adminLoading } = useIsOrgAdmin();
  const [modules, setModules] = useState<ModuleKey[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id || !organizationId) {
      setModules(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const load = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from('user_profile')
          .select('job_role_id, job_roles(modules, active)')
          .eq('auth_id', user.id)
          .eq('organization_id', organizationId)
          .maybeSingle();
        if (cancelled) return;
        const role = data?.job_roles;
        // Sem cargo, cargo inativo ou erro: não restringe módulos.
        if (error || !data?.job_role_id || !role || role.active === false) {
          setModules(null);
        } else {
          setModules((role.modules as ModuleKey[]) ?? ALL_MODULE_KEYS);
        }
      } catch {
        if (!cancelled) setModules(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id, organizationId]);

  const value = useMemo<ModuleAccessValue>(() => {
    const access: ModuleAccess = { loading: loading || adminLoading, isAdmin, modules };
    return { ...access, canAccess: (s: string) => canAccessSection(access, s) };
  }, [loading, adminLoading, isAdmin, modules]);

  return <ModuleAccessContext.Provider value={value}>{children}</ModuleAccessContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useModuleAccess = () => useContext(ModuleAccessContext);

export const ModuleGate: React.FC<{ section: string; children: React.ReactNode }> = ({ section, children }) => {
  const { canAccess } = useModuleAccess();
  return canAccess(section) ? <>{children}</> : <NoAccess />;
};
